import React, { useEffect, useState } from "react";
import { Flex, Box, AspectRatio, Text, type IconProps } from "@chakra-ui/react";
import Image, { type StaticImageData } from "next/image";
import { stagger, useAnimate, useInView } from "framer-motion";
import * as Sentry from "@sentry/nextjs";
import { FacebookIcon, SnapchatIcon, TikTokIcon } from "ui/icons/integrations";
import mobileImage from "../../../../../../public/assets/platform/customer-studio/hero-mobile.png";
import firstSourceRowImage from "../../../../../../public/assets/platform/customer-studio/hero-source-row-1.png";
import secondSourceRowImage from "../../../../../../public/assets/platform/customer-studio/hero-source-row-2.png";
import thirdSourceRowImage from "../../../../../../public/assets/platform/customer-studio/hero-source-row-3.png";
import { MovingBallIcon } from "./moving-ball";

const rowHeight = "82px";
const scaleFadeIn = { opacity: 1, scale: 1 };
const scaleFadeOut = { opacity: 0, scale: 0.95 };
const moveRight = { opacity: [0, 1, 0], x: ["-20%", "300%"] };
const resetTiming = { duration: 0.05 };
const fontSizes = { base: "sm", lg: "xs", xl: "sm" };

type Source = {
  name: string;
  image: StaticImageData;
};

type Audience = {
  name: string;
  icon: (props: IconProps) => JSX.Element;
};

const sources: Source[] = [
  {
    name: "Indie music fans",
    image: firstSourceRowImage,
  },
  {
    name: "Concert attendees",
    image: secondSourceRowImage,
  },
  {
    name: "Newsletter subscribers",
    image: thirdSourceRowImage,
  },
];

const audiences: Audience[] = [
  {
    name: "Add to Audience",
    icon: TikTokIcon,
  },
  {
    name: "Conversion Action",
    icon: FacebookIcon,
  },
  {
    name: "Add to Suppression",
    icon: SnapchatIcon,
  },
];

function useHeroAnimation(isActive: boolean) {
  const [scope, animate] = useAnimate();

  useEffect(() => {
    const triggerAnimation = async () => {
      try {
        await animate(
          isActive
            ? [
                // Animation sequence
                [".audience-item", scaleFadeIn, { at: "+0.5", type: "spring", delay: stagger(0.4) }],
                [".connector-line", { opacity: [0, 1], x: ["0%", "100%"] }, { at: "-1.6", delay: stagger(0.4) }],
                ["#ball-0", moveRight, { at: "-0.5", duration: 1.1, ease: "easeInOut" }],
                ["#ball-1", moveRight, { at: "-0.4", duration: 0.9, ease: "easeInOut" }],
                ["#ball-2", moveRight, { at: "-0.5", duration: 1.2, ease: "easeInOut" }],
              ]
            : [
                // Reset
                [".audience-item", scaleFadeOut, resetTiming],
                [".connector-line", { opacity: 0, x: "0%" }, resetTiming],
              ],
        );
        if (isActive) {
          await animate(
            // Infinite loop after main sequence
            [
              ["#ball-0", moveRight, { duration: 1.1, ease: "easeInOut" }],
              ["#ball-1", moveRight, { at: "-0.4", duration: 0.9, ease: "easeInOut" }],
              ["#ball-2", moveRight, { at: "-0.5", duration: 1.2, ease: "easeInOut" }],
            ],
            { repeat: Number.POSITIVE_INFINITY, repeatDelay: -0.2 },
          );
        }
      } catch (error) {
        Sentry.captureException(error);
      }
    };

    // Do not need `await` because there is no action after the promise
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    triggerAnimation();
  }, [isActive, animate]);

  return scope;
}

export function HeroAnimation() {
  const [animationActive, setAnimationActive] = useState(false);
  const scope = useHeroAnimation(animationActive);
  const isInView = useInView(scope, { amount: 0.25, once: true });

  useEffect(() => {
    setAnimationActive(isInView);
  }, [isInView]);

  return (
    <Flex ref={scope} boxSize="100%" fontFamily="inter" maxW="600px" mx="auto" pos="relative">
      <AspectRatio display={{ base: "block", md: "none" }} ratio={3 / 2} pos="relative" w="100%">
        <Image
          priority
          fill
          src={mobileImage}
          alt="Hightouch Customer Studio"
          sizes="100%"
          style={{ objectFit: "contain" }}
        />
      </AspectRatio>

      <Flex display={{ base: "none", md: "flex" }} align="end" justify="space-between" w="100%">
        <Box flex={1} bg="white" borderRadius="xl" boxShadow="0px 2px 6px 0px rgba(0, 0, 0, 0.12)" p={5}>
          <Flex
            align="center"
            color="product.gray.500"
            fontSize={fontSizes}
            fontWeight="medium"
            gap={3}
            justify="space-between"
            mr={2}
          >
            <Text borderRadius="6px" bg="teal" color="white" px={2.5} py={1.5}>
              Audiences
            </Text>
            <Text>Attributes</Text>
            <Text>Events</Text>
          </Flex>

          <Flex direction="column" gap={3} mt={5}>
            {sources.map(({ name, image }) => (
              <Flex
                key={name}
                bg="white"
                border="1px"
                borderColor="grass"
                borderRadius="6px"
                direction="column"
                gap={2}
                h={rowHeight}
                p={3}
                zIndex={1}
              >
                <Text fontSize={fontSizes} fontWeight="medium">
                  {name}
                </Text>
                <AspectRatio ratio={20 / 3} pos="relative" w="100%" maxW={{ base: "200px", lg: "170px", xl: "200px" }}>
                  <Image fill src={image} alt={`Faces of ${name}.`} sizes="100%" style={{ objectFit: "contain" }} />
                </AspectRatio>
              </Flex>
            ))}
          </Flex>
        </Box>

        <Flex direction="column" gap={3} flex={1} pb={{ base: 5, lg: 3, xl: 5 }} pl={8}>
          {audiences.map(({ name, icon }, index) => (
            <Flex key={name} pos="relative">
              <Box
                pos="absolute"
                h="1px"
                w="60px" // Width = audience container left padding + source right padding + 1px for the border
                right="100%"
                top="50%"
                bottom="50%"
                transform="translateY(-50%)"
              >
                <Box className="connector-line" pos="absolute" bg="grass" h="1px" right="100%" w="100%" opacity={0} />
                <MovingBallIcon id={`ball-${index}`} pos="absolute" right="110%" top="-3px" />
              </Box>

              <Flex
                className="audience-item"
                align="center"
                bg="rgba(255, 255, 255, 0.5)"
                border="1px"
                borderColor="grass"
                borderRadius="6px"
                h={rowHeight}
                justify="center"
                p={{ base: 3, lg: 2.5, xl: 3 }}
                pos="relative"
                w="100%"
              >
                <Flex
                  align="center"
                  bg="white"
                  borderRadius="6px"
                  boxSize="100%"
                  gap={3}
                  p={{ base: 3, lg: 2.5, xl: 3 }}
                  zIndex={1}
                >
                  <Box as={icon} boxSize={{ base: 7, lg: 6, xl: 7 }} />
                  <Box>
                    <Text fontSize={fontSizes} fontWeight="medium">
                      {name}
                    </Text>
                    <Text color="text.secondary" fontSize="xs" fontWeight="medium">
                      Today
                    </Text>
                  </Box>
                </Flex>
              </Flex>
            </Flex>
          ))}
        </Flex>
      </Flex>
    </Flex>
  );
}
